import type {ComponentProps, ForwardRefExoticComponent, Key, ReactElement, RefAttributes} from 'react'
import {ActionMenu} from '@primer/react-brand/esm'

import {getAnalyticsEvent} from '../../lib/utils/analytics'
import {ActionMenuLinkItem} from './ActionMenuLinkItem'
import type {OSAwareDownloadAction, ResolvedDownloadActions} from './resolve-download-actions'
import {
  useOSAwareDownloadActionMenu,
  type OSAwareDownloadActionMenuController,
} from './use-os-aware-download-action-menu'

/**
 * `ActionMenu.Button` forwards these anchor attributes at runtime, but its
 * exported props omit them when `as="a"`.
 */
const ActionMenuAnchorButton = ActionMenu.Button as unknown as ForwardRefExoticComponent<
  ComponentProps<typeof ActionMenu.Button> &
    Pick<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel'> & {
      'data-analytics-event'?: string
    } & RefAttributes<HTMLAnchorElement>
>

export type OSAwareDownloadActionMenuProps = {
  actions: readonly OSAwareDownloadAction[]
  menuLabel: string
  analyticsLocation?: string
  analyticsContext?: string
  size?: 'small' | 'medium'
  variant?: ComponentProps<typeof ActionMenu.Button>['variant']
}

export type RenderOSAwareDownloadActionMenuOptions = {
  controller?: OSAwareDownloadActionMenuController
  menuLabel?: string
  analyticsLocation?: string
  analyticsContext?: string
  size?: 'small' | 'medium'
  variant?: ComponentProps<typeof ActionMenu.Button>['variant']
  elementKey?: Key
}

export type OSAwareDownloadActionMenuElement = ReactElement<ComponentProps<typeof ActionMenu>, typeof ActionMenu>

function renderDownloadActionMenuItem(
  item: ResolvedDownloadActions['items'][number],
  analyticsLocation?: string,
  analyticsContext?: string,
) {
  const {action} = item
  const analyticsEvent = getAnalyticsEvent({
    action: action.label,
    tag: 'link',
    location: analyticsLocation,
    context: analyticsContext,
  })

  return (
    <ActionMenuLinkItem
      key={action.id}
      href={action.href}
      analyticsEvent={analyticsEvent}
      openInNewTab={action.openInNewTab}
    >
      {action.label}
    </ActionMenuLinkItem>
  )
}

/**
 * Returns Primer Brand's `ActionMenu` element directly. Brand ButtonGroups
 * filter immediate children by exact element type, so callers inside a
 * ButtonGroup should compose this helper with `useOSAwareDownloadActionMenu`.
 */
export function renderOSAwareDownloadActionMenu(
  options: RenderOSAwareDownloadActionMenuOptions & {
    controller: OSAwareDownloadActionMenuController
    menuLabel: string
  },
): OSAwareDownloadActionMenuElement
export function renderOSAwareDownloadActionMenu(
  options: RenderOSAwareDownloadActionMenuOptions,
): OSAwareDownloadActionMenuElement | null
export function renderOSAwareDownloadActionMenu({
  controller,
  menuLabel,
  analyticsLocation,
  analyticsContext,
  size,
  variant,
  elementKey,
}: RenderOSAwareDownloadActionMenuOptions): OSAwareDownloadActionMenuElement | null {
  if (!controller || !menuLabel) return null

  const {primary, items, actionMenuId} = controller
  const primaryOpenInNewTab = Boolean(primary.action.openInNewTab)

  return (
    <ActionMenu key={elementKey} id={actionMenuId} mode="split-button" size={size}>
      <ActionMenuAnchorButton
        as="a"
        href={primary.action.href}
        variant={variant}
        target={primaryOpenInNewTab ? '_blank' : undefined}
        rel={primaryOpenInNewTab ? 'noopener noreferrer' : undefined}
        {...getAnalyticsEvent({
          action: primary.action.label,
          tag: 'link',
          location: analyticsLocation,
          context: analyticsContext,
        })}
      >
        {primary.action.label}
      </ActionMenuAnchorButton>
      <ActionMenu.Overlay aria-label={menuLabel}>
        {items.map(item => renderDownloadActionMenuItem(item, analyticsLocation, analyticsContext))}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

/**
 * Convenience wrapper for use outside Primer Brand ButtonGroups.
 */
export function OSAwareDownloadActionMenu({
  actions,
  menuLabel,
  analyticsLocation,
  analyticsContext,
  size,
  variant,
}: OSAwareDownloadActionMenuProps) {
  const controller = useOSAwareDownloadActionMenu(actions, menuLabel)

  return renderOSAwareDownloadActionMenu({
    controller,
    menuLabel,
    analyticsLocation,
    analyticsContext,
    size,
    variant,
  })
}
